import {
  Subscription,
  Subject,
  PartialObserver,
  BehaviorSubject,
  Subscribable,
  Unsubscribable
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { ITimestampedUpdate, NO_VALUE } from './values.exchange.provider';
import { isEqual } from 'lodash';
/**
 * A simple wrapper around BehaviorSubject. Helps to troubleshoot subscription and notification problems.
 */
export class ListenersAggregate<T> implements Subscribable<T> {
  latestValue: ITimestampedUpdate;
  previousValue: ITimestampedUpdate;
  // listeners that want to be notified on new values from producer
  listeners: Subject<any>;

  constructor(stateless: boolean) {
    if (!stateless) {
      this.listeners = new BehaviorSubject<any>(NO_VALUE);
    } else {
      this.listeners = new Subject<any>();
    }
  }
  subscribe(observer?: PartialObserver<T>): Unsubscribable;
  subscribe(
    next?: (value: T) => void,
    error?: (error: any) => void,
    complete?: () => void
  ): Unsubscribable;
  subscribe(nextOrObserver?: any, b?: any, c?: any): Unsubscribable {
    return this.listeners
      .pipe(
        tap(() => {
          // nothing to be done. left here for your convenience to debug messages
        })
      )
      .subscribe(nextOrObserver);
  }
  /**
   * Quite often, widgets keep firing multiple updates without any real change to published value.
   * As result, all listeners were dutifully kicking new queries to backend (which is costly).
   * The new logic is comparing previous and latest values - and in case there is no change, no
   * event will be emitted.
   */
  nextValue(value: any) {
    this.previousValue = this.latestValue;
    this.latestValue = value;
    if (this.noChanges(this.previousValue, this.latestValue)) {
      return;
    }
    this.listeners.next(value);
  }
  noChanges(prev, latest) {
    const v1 = this.unpackRawValue(prev);
    const v2 = this.unpackRawValue(latest);
    return isEqual(v1, v2);
  }
  unpackRawValue(value) {
    if (value && value.vexTimestamp) {
      return value.rawValue;
    }
    return value;
  }
}
